import { Accordion, Button, ControlledInput, Label, Select, SelectTags, Tag } from 'components';
import { InfoHollowIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { KeysEnum } from 'types/KeysEnum';
import { cn } from 'utils/cn';
import { useContentGenerationContext } from '../_context/ContentGenerationContext';

const ContentBriefMenu: FC = () => {
  const t = useTranslations();

  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollPercent, setScrollPercent] = useState(1);

  useEffect(() => {
    const container = containerRef.current;

    const handleScroll = () => {
      if (container) {
        const scrollTop = container?.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        const totalScroll = scrollHeight - clientHeight;
        const scrollFraction = scrollTop / totalScroll;

        setScrollPercent(1 - scrollFraction * 0.9);
      }
    };

    container?.addEventListener('scroll', handleScroll);

    return () => container?.removeEventListener('scroll', handleScroll);
  }, []);

  const {
    generatedContent,
    contentType,
    optionsLanguages,
    optionsContexts,
    optionsContentType,
    optionsContentFormat,
    optionsFilterTags,
    optionsStyleGuidesCardFields,
    contextsDisabled,
    contextIds,
    selectedTagId,
    contextsIncluded,
    isGeneratingContent,
    isSavingChanges,
    isCreatingTranslation,
    isNotInitialized,
    control,
    styleGuide,
    errors,
    goCreateNewTranslation,
    handleSelectContextIds,
    handleDeleteContextIds,
    setSelectedTagId,
    handleGenerateContent,
    handleSaveChanges,
    // handleCreateNewTranslation,
    handleSelectStyleGuides
  } = useContentGenerationContext();

  return (
    <div className="flex flex-1 flex-col justify-between overflow-auto pb-4">
      <div
        className={cn(
          'flex h-full  flex-1 flex-col overflow-scroll',
          isNotInitialized ? 'max-h-[calc(90vh_-_180px)]' : 'max-h-[calc(90vh_-_180px)]'
        )}
        ref={containerRef}
      >
        <div className="px-4">
          <Label>
            {t('labels.topic')}
            <span className="text-secondary-red">*</span>
          </Label>
          <ControlledInput
            control={control}
            error={errors.topic}
            type="textarea"
            name={KeysEnum.Topic}
            className="mt-1"
            placeholder={t('placeholders.topic')}
            disabled={!isNotInitialized}
            optionalParams={{ rows: 3 }}
          />
        </div>
        <div className="px-4">
          <div className="flex">
            <Label>
              {t('labels.target_language')}
              <span className="text-secondary-red">*</span>
            </Label>
            {false && (
              <a
                data-tooltip-id="tolq-info-top-end"
                data-tooltip-html={t('tooltips.multiple_languages')}
              >
                <InfoHollowIcon className="scale-50 cursor-pointer" />
              </a>
            )}
          </div>
          <ControlledInput
            control={control}
            error={errors.language}
            placeholder={t('placeholders.select_target_language')}
            type="combobox"
            name={KeysEnum.Language}
            classNameParent="w-full select-none"
            options={optionsLanguages}
            disabled={!isNotInitialized}
          />
        </div>
        <div className="px-4">
          <Label>
            {t('labels.content_type')}
            <span className="text-secondary-red">*</span>
          </Label>
          <ControlledInput
            control={control}
            error={errors.contentType}
            type="select"
            name={KeysEnum.ContentType}
            options={optionsContentType}
            className="col-span-2 !mb-0"
            placeholder={t('placeholders.select_content_type')}
            disabled={!isNotInitialized}
          />
        </div>
        <div className="px-4">
          <Label>
            {t('labels.content_format')}
            {!!contentType && <span className="text-secondary-red">*</span>}
          </Label>
          <ControlledInput
            control={control}
            error={errors.contentFormat}
            type="select"
            name={KeysEnum.ContentFormat}
            options={optionsContentFormat}
            className="col-span-2 !mb-0"
            placeholder={t('placeholders.select_content_format')}
            disabled={!contentType || !isNotInitialized}
          />
        </div>
        {false && (
          <div className="px-4">
            <div className="flex">
              <Label>{t('labels.data_contexts')}</Label>
              <a
                data-tooltip-id="tolq-info-top-start"
                data-tooltip-html={t('chat.info.data_contexts')}
              >
                <InfoHollowIcon className="scale-50 cursor-pointer" />
              </a>
            </div>
            <SelectTags
              options={optionsContexts}
              onChange={handleSelectContextIds}
              placeholder={
                contextsDisabled
                  ? t('placeholders.data_contexts_no_more')
                  : t('placeholders.data_contexts')
              }
              selectedOptions={contextIds}
              showSelected={false}
              placeholderSearch={t('placeholders.search_files')}
              categoryOptions={optionsFilterTags}
              onSelectCategory={setSelectedTagId as (value: string) => void}
              category={selectedTagId}
              className="mb-2"
              classNameTags="!bg-primary-blue-900 !rounded-full"
              classNameTagsTypography="!text-primary-blue-100"
              noClose
              disabled={!optionsContexts?.length || !isNotInitialized}
              wideHeight
              cleanOnSelect
            />
            {!!contextsIncluded?.length && (
              <div
                className={cn(
                  'mb-2 flex flex-col gap-2',
                  contextsDisabled ? 'pointer-events-none !cursor-not-allowed' : ''
                )}
              >
                {contextsIncluded?.map(({ filename, id }) => {
                  const fileNameisBig = (filename?.length ?? 0) > 50;

                  return (
                    <Tag
                      key={id}
                      onRemove={contextsDisabled ? undefined : handleDeleteContextIds(id)}
                      className={cn(
                        'bg-tertiary-600',
                        fileNameisBig ? '!items-start' : '',
                        contextsDisabled ? 'hover:!scale-[1]' : 'hover:!scale-[1.01]'
                      )}
                      classNameTypography="!text-tertiary-200"
                      classNameCloseIcon="!text-tertiary-200"
                      classNameRemove={cn(fileNameisBig ? '!mt-1' : '')}
                    >
                      {filename}
                    </Tag>
                  );
                })}
              </div>
            )}
            {!contextsIncluded?.length && (
              <div
                className={cn(
                  'mb-2 flex flex-col gap-2',
                  contextsDisabled ? 'pointer-events-none !cursor-not-allowed' : ''
                )}
              >
                {generatedContent.uploadedFiles?.map(({ filename }) => {
                  const fileNameisBig = (filename?.length ?? 0) > 50;

                  return (
                    <Tag
                      key={filename}
                      className={cn(
                        'bg-tertiary-600',
                        fileNameisBig ? '!items-start' : '',
                        contextsDisabled ? 'hover:!scale-[1]' : 'hover:!scale-[1.01]'
                      )}
                      classNameTypography="!text-tertiary-200"
                      classNameCloseIcon="!text-tertiary-200"
                      classNameRemove={cn(fileNameisBig ? '!mt-1' : '')}
                    >
                      {filename}
                    </Tag>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="px-4">
          <Label>{t('labels.additional_instructions')}</Label>
          <ControlledInput
            control={control}
            type="textarea"
            name={KeysEnum.AdditionalInstructions}
            className="mt-1"
            placeholder={t('placeholders.enter_additional_instructions')}
            disabled={!isNotInitialized}
            optionalParams={{ rows: 3 }}
          />
        </div>
        <div className="px-4">
          <Accordion
            title={t('labels.style_guide')}
            defaultOpen
            className="!my-0 !mt-4"
            classNameChildren="!p-2"
            classNameTitle="!pl-2 !pt-0"
          >
            {optionsStyleGuidesCardFields.map(({ label, name, options, placeholder }) => (
              <Fragment key={name}>
                <Label>{label}</Label>
                <Select
                  key={name}
                  options={options}
                  className="col-span-2 !mb-0"
                  disabled={!isNotInitialized}
                  selectedValue={styleGuide?.[name] ?? undefined}
                  onChange={handleSelectStyleGuides(name)}
                  placeholder={placeholder}
                />
              </Fragment>
            ))}
          </Accordion>
        </div>
        {false && (
          <div className="px-4">
            <Label>{t('labels.content_length')}</Label>
            <ControlledInput
              control={control}
              type="number"
              name={KeysEnum.ContentLength}
              className="no-arrows mt-1"
              placeholder={t('placeholders.e_g_two_hundred_words')}
              endAdornment={
                <Label className="absolute -left-10 -top-2.5">{t('placeholders.words')}</Label>
              }
              disabled={!isNotInitialized}
              optionalParams={{ classNameCloseIcon: '!top-[17px] !right-[4.25rem]' }}
            />
          </div>
        )}
        <div className="w-full pt-8" />
      </div>
      <div className="relative flex flex-col items-center px-4">
        <div
          className={cn(
            'absolute -top-12 w-full bg-transparent py-6',
            'shadow-[rgba(0,0,0,0.1)_0px_-45px_40px_-40px_inset]',
            'transition-all'
          )}
          style={{
            opacity: scrollPercent
          }}
        />
        <div className="w-full pt-4" />
        {isNotInitialized && (
          <Button
            color="primary"
            size="medium"
            label={t('buttons.generate_content')}
            onClick={handleGenerateContent}
            className="w-full !px-4"
            loading={isGeneratingContent}
            disabled={isGeneratingContent}
          />
        )}
        {!isNotInitialized && (
          <>
            <Button
              color="primary"
              size="medium"
              label={t('buttons.save_changes')}
              onClick={handleSaveChanges}
              className="w-full !px-4"
              loading={isSavingChanges}
              disabled={isSavingChanges}
            />
            <div className="w-full pt-4" />
            <Button
              color="secondary"
              variant="outlined"
              size="medium"
              className="w-full !px-4"
              label={t('buttons.create_translation')}
              onClick={goCreateNewTranslation}
              classNameLoading="border-tertiary-400 border-1 border-t-transparent top-[2px]"
              // loading={isCreatingTranslation}
              // disabled={isCreatingTranslation}
              startAdornment={
                <a
                  data-tooltip-id="tolq-info-left"
                  data-tooltip-html={t.raw('content_generation.content_brief.tooltip')}
                >
                  <InfoHollowIcon className="scale-50 cursor-pointer" />
                </a>
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ContentBriefMenu;
